import { ValidationErrorDetail, ValidationStatusEntity } from 'src/models/XptGenericModels';
import { ValidationService } from './ValidationService';
import ForecastValidationMessages from '../forecast-utils/ValidationMessages';
import { ForecastGridFixedFields } from '../forecast-utils/ForecastGridConstants';
import { logger } from 'src/analytics/KatalLogger';
import { UserAccessEntity, UserAccessForCurrentBusinessGroup } from 'src/models/XptUsersModel';
import { CorpSegmentNames } from 'src/constants/corp-segment-constants';
import {
  getUserCostCenters,
  getUserCostCentersFromUserAccessEntitiesList
} from 'src/features/business-group/access-authorization/redux/AccessAuthorizationUtils';

/**
 * Service for validating user authorization in forecast templates
 */
export class AuthorizationValidationService extends ValidationService {
  /**
   * Validates that the user has authorization to modify the rows.
   * @param modifiedRows The array of modified rows to validate.
   * @param userAlias The alias of the current user.
   * @param isAdminUser Whether the current user is an admin.
   * @returns Promise with validation status
   */
  public async validateUnAuthorizedRows(modifiedRows: any[], userAlias: string, isAdminUser: boolean): Promise<ValidationStatusEntity> {
    const validationErrorDetails: ValidationErrorDetail[] = [];
    logger.info(`Validating unauthorized rows for ${modifiedRows.length} records`);

    modifiedRows.forEach((row, rowIndex) => {
      const budgetOwner = row[ForecastGridFixedFields.BudgetOwner.value];
      if (!isAdminUser && budgetOwner !== userAlias) {
        validationErrorDetails.push({
          rowIndex: rowIndex + 1,
          message: `Budget Owners can only modify their own rows. Expected Budget Owner: ${userAlias}. Found ${budgetOwner} ${this.getLineItemIdMessagePart(
            row,
            ForecastGridFixedFields.XptLineItemId.value
          )}.`
        });
      }
    });

    // Log validation errors if any
    this.logValidationErrors('Unauthorized Rows', validationErrorDetails);

    // Return appropriate validation status
    if (validationErrorDetails.length > 0) {
      return this.createErrorStatus(
        ForecastValidationMessages.UNAUTHORIZED_ROWS_VALIDATION_FAILED,
        ForecastValidationMessages.UNAUTHORIZED_ROWS_VALIDATION_DEFAULT,
        validationErrorDetails
      );
    }

    return this.createSuccessStatus(
      ForecastValidationMessages.UNAUTHORIZED_ROWS_VALIDATION_SUCCESS,
      ForecastValidationMessages.UNAUTHORIZED_ROWS_VALIDATION_DEFAULT
    );
  }

  /**
   * Validates that the cost centers in the file are authorized for the current user.
   * @param forecastTemplateGridData The grid data to validate.
   * @param userAccessForCurrentBusinessGroup The user's access for the current business group.
   * @returns Promise with validation status
   */
  public async validateCCsInvolvedInExcel(
    forecastTemplateGridData: any[],
    userAccessForCurrentBusinessGroup: UserAccessForCurrentBusinessGroup
  ): Promise<ValidationStatusEntity> {
    try {
      const currentUserCCs = new Set(getUserCostCenters(userAccessForCurrentBusinessGroup));
      const costCentersFromUploadingFile = new Set<string>();

      forecastTemplateGridData.forEach((row) => {
        const costCenter = `${row[CorpSegmentNames.COST_CENTER]}`;
        if (costCenter) costCentersFromUploadingFile.add(costCenter);
      });

      const invalidCCsFromFile = Array.from(costCentersFromUploadingFile).filter((cc) => !currentUserCCs.has(cc));

      if (invalidCCsFromFile.length > 0) {
        this.logValidationErrors(
          "Cost Center's",
          invalidCCsFromFile.map((cc) => ({ message: cc }))
        );
        return this.createErrorStatus(
          ForecastValidationMessages.CC_AUTHORIZATION_VALIDATION_FAILED,
          ForecastValidationMessages.CC_AUTHORIZATION_VALIDATION_DEFAULT,
          invalidCCsFromFile.map((cc) => ({ message: cc }))
        );
      }

      return this.createSuccessStatus(
        ForecastValidationMessages.CC_AUTHORIZATION_VALIDATION_SUCCESS,
        ForecastValidationMessages.CC_AUTHORIZATION_VALIDATION_DEFAULT
      );
    } catch (error: any) {
      logger.error('Error validating Cost Centers:', error);
      return this.createErrorStatus(
        ForecastValidationMessages.CC_AUTHORIZATION_VALIDATION_ERROR,
        ForecastValidationMessages.CC_AUTHORIZATION_VALIDATION_DEFAULT,
        [{ message: error.message || 'Unknown error validating cost centers' }]
      );
    }
  }

  /**
   * Validates that the budget owners in the file are authorized.
   * @param forecastTemplateGridData The grid data to validate.
   * @param userAccessForCurrentBusinessGroup The user's access for the current business group.
   * @returns Promise with validation status
   */
  public async validateBudgetOwnersInvolvedInExcel(
    forecastTemplateGridData: any[],
    userAccessForCurrentBusinessGroup: UserAccessForCurrentBusinessGroup
  ): Promise<ValidationStatusEntity> {
    try {
      if (!userAccessForCurrentBusinessGroup.isBudgetOwner) {
        return this.createSuccessStatus(
          ForecastValidationMessages.BUDGET_OWNER_VALIDATION_SUCCESS,
          ForecastValidationMessages.BUDGET_OWNER_VALIDATION_DEFAULT
        );
      } else {
        const budgetOwnersFromUploadingFile = new Set<string>();
        const currentUser = userAccessForCurrentBusinessGroup.user_alias;

        forecastTemplateGridData.forEach((row) => {
          const budgetOwnerInThisRow = row[ForecastGridFixedFields.BudgetOwner.value];
          if (budgetOwnerInThisRow) budgetOwnersFromUploadingFile.add(budgetOwnerInThisRow);
        });

        const invalidBudgetOwnersFromFile = Array.from(budgetOwnersFromUploadingFile).filter((budgetOwner) => currentUser !== budgetOwner);

        if (invalidBudgetOwnersFromFile.length > 0) {
          this.logValidationErrors(
            "Budget Owner's",
            invalidBudgetOwnersFromFile.map((bo) => ({ message: bo }))
          );
          return this.createErrorStatus(
            ForecastValidationMessages.BUDGET_OWNER_VALIDATION_FAILED,
            ForecastValidationMessages.BUDGET_OWNER_VALIDATION_DEFAULT,
            invalidBudgetOwnersFromFile.map((bo) => ({ message: bo }))
          );
        } else {
          return this.createSuccessStatus(
            ForecastValidationMessages.BUDGET_OWNER_VALIDATION_SUCCESS,
            ForecastValidationMessages.BUDGET_OWNER_VALIDATION_DEFAULT
          );
        }
      }
    } catch (error: any) {
      logger.error('Error validating Budget Owners:', error);
      return this.createErrorStatus(
        ForecastValidationMessages.BUDGET_OWNER_VALIDATION_ERROR,
        ForecastValidationMessages.BUDGET_OWNER_VALIDATION_DEFAULT,
        [{ message: error.message || 'Unknown error validating budget owners' }]
      );
    }
  }

  /**
   * Validates that the budget owner and cost center combinations are valid.
   * @param forecastTemplateGridData The grid data to validate.
   * @param xptUsersOfCurrentDataClassification The users of the current data classification.
   * @param userAccessForCurrentBusinessGroup The user's access for the current business group.
   * @returns Promise with validation status
   */
  public async validateBudgetOwnerAndCostCenterCombination(
    forecastTemplateGridData: any[],
    xptUsersOfCurrentDataClassification: UserAccessEntity[],
    userAccessForCurrentBusinessGroup: UserAccessForCurrentBusinessGroup
  ): Promise<ValidationStatusEntity> {
    try {
      const validationErrorDetails: ValidationErrorDetail[] = [];
      logger.info(`Validating budget owner and cc combination for ${forecastTemplateGridData.length} records`);

      const currentUserCCs = getUserCostCenters(userAccessForCurrentBusinessGroup);

      forecastTemplateGridData.forEach((row, rowIndex) => {
        const budgetOwner = `${row[ForecastGridFixedFields.BudgetOwner.value]}`;
        const costCenter = `${row[CorpSegmentNames.COST_CENTER]}`;

        // If Budget Owner field is current user, then check with current users CC's
        if (budgetOwner === userAccessForCurrentBusinessGroup.user_alias) {
          if (!currentUserCCs.includes(costCenter)) {
            validationErrorDetails.push({
              rowIndex: rowIndex + 1,
              message: `Budget Owner ${budgetOwner} and Cost Center ${costCenter} combination is invalid.`
            });
          }
        } else {
          if (budgetOwner && costCenter) {
            const givenUsersCCs = getUserCostCentersFromUserAccessEntitiesList(budgetOwner, xptUsersOfCurrentDataClassification);
            if (!givenUsersCCs.includes(costCenter)) {
              validationErrorDetails.push({
                rowIndex: rowIndex + 1,
                message: `Budget Owner ${budgetOwner} and Cost Center ${costCenter} combination is invalid.`
              });
            }
          }
        }
      });

      // Log validation errors if any
      this.logValidationErrors('Budget Owner & Cost Center Combination', validationErrorDetails);

      // Return appropriate validation status
      if (validationErrorDetails.length > 0) {
        return this.createErrorStatus(
          ForecastValidationMessages.BUDGET_OWNER_AND_COST_CENTER_VALIDATION_FAILED,
          ForecastValidationMessages.BUDGET_OWNER_AND_COST_CENTER_VALIDATION_DEFAULT,
          validationErrorDetails
        );
      }

      return this.createSuccessStatus(
        ForecastValidationMessages.BUDGET_OWNER_AND_COST_CENTER_VALIDATION_SUCCESS,
        ForecastValidationMessages.BUDGET_OWNER_AND_COST_CENTER_VALIDATION_DEFAULT
      );
    } catch (error: any) {
      logger.error('Error validating Budget Owner & Cost Center Mapping:', error);
      return this.createErrorStatus(
        ForecastValidationMessages.BUDGET_OWNER_AND_COST_CENTER_VALIDATION_ERROR,
        ForecastValidationMessages.BUDGET_OWNER_AND_COST_CENTER_VALIDATION_DEFAULT,
        [{ message: error.message || 'Unknown error validating budget owner and cost center combination' }]
      );
    }
  }
}

// Export singleton instance
export const authorizationValidationService = new AuthorizationValidationService();
